import React from 'react';
import { Card, Form, InputNumber, Select, Typography } from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { newCreationFlowEvents } from '@/common/Scribe/EventsList';
import AxaWarningAlert from '@/features/experiment/components/axa-warning-alert';
import {
  getAssignmentStrategy,
  getTreatmentName,
  isViewMode,
  TreatmentDefaultColors,
} from '@/features/experiment/constants';
import { useExperimentContext } from '@/features/experiment/contexts/experiment-context';
import {
  getSequentialTimesliceSliceWindow,
  isAxaIncompatibleSliceWindow,
} from '@/features/experiment/utils';
import { TimeSliceTimeUnit } from '@/pages/ExperimentFormV2/helper/consts';

const TimeSliceValuesHeader = () => {
  const form = Form.useFormInstance();

  return (
    <thead>
      <tr>
        <th
          style={{
            padding: '4px 0',
            paddingRight: 16,
            background: '#FFF',
            borderRight: '1px solid #E8E8E8',
          }}
        >
          <div style={{ width: 320, textAlign: 'left' }} />
        </th>
        <Form.List name="configurations">
          {(configurations) =>
            configurations.map((configuration) => {
              if (configuration.key > 0) return null;

              return (
                <Form.List
                  name={[configuration.name, 'variables']}
                  key={configuration.key}
                >
                  {(variables) =>
                    variables.map((variable) => {
                      if (variable.key > 0) return null;

                      return (
                        <Form.List
                          key={variable.key}
                          name={[variable.name, 'choices']}
                        >
                          {(choices) =>
                            choices.map((choice) => {
                              return (
                                <th
                                  key={choice.key}
                                  style={{
                                    padding: '4px 16px',
                                    borderRight: '1px solid #e8e8e8',
                                    position: 'relative',
                                    zIndex: 0,
                                    minWidth: 400,
                                    maxWidth: 400,
                                  }}
                                >
                                  <Tag
                                    color={`${TreatmentDefaultColors[choice.key]}1A`}
                                    style={{
                                      padding: '6px 16px',
                                      color: '#3D3D3D',
                                      margin: 0,
                                      width: '100%',
                                      minHeight: 40,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 8,
                                        }}
                                      >
                                        <TreatmentDot
                                          color={
                                            TreatmentDefaultColors[choice.key]
                                          }
                                        />
                                        {getTreatmentName(
                                          form.getFieldValue([
                                            'configurations',
                                            configuration.name,
                                            'variables',
                                            variable.name,
                                            'choices',
                                            choice.name,
                                          ]),
                                          choice.key,
                                        )}
                                      </div>
                                    </div>
                                  </Tag>
                                </th>
                              );
                            })
                          }
                        </Form.List>
                      );
                    })
                  }
                </Form.List>
              );
            })
          }
        </Form.List>
      </tr>
    </thead>
  );
};

const TimeSliceValuesBody = () => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { experiment, configurations } = experimentContext;
  const experimentStrategy = getAssignmentStrategy(configurations);

  const form = Form.useFormInstance();

  const trackTimeSliceValueChange = (choiceData) => {
    scribe.sendEvent(newCreationFlowEvents.changeTimesliceValue.name, {
      experiment_name: experiment?.name,
      assignment_strategy: experimentStrategy,
      treatment_id: choiceData.treatment,
      time: choiceData.span || 0,
      time_unit: choiceData.spanUnit || TimeSliceTimeUnit.SECONDS,
      buffer_time: choiceData.spanBuffer || 0,
      buffer_time_unit: choiceData.spanBufferUnit || TimeSliceTimeUnit.SECONDS,
      STATE_NAME: newCreationFlowEvents.changeTimesliceValue.state,
    });
  };

  const trackTimeSliceUnitChange = (choiceData) => {
    scribe.sendEvent(newCreationFlowEvents.changeTimesliceUnit.name, {
      experiment_name: experiment?.name,
      assignment_strategy: experimentStrategy,
      treatment_id: choiceData?.treatment,
      time: choiceData.span || 0,
      time_unit: choiceData.spanUnit || TimeSliceTimeUnit.SECONDS,
      buffer_time: choiceData.spanBuffer || 0,
      buffer_time_unit: choiceData.spanBufferUnit || TimeSliceTimeUnit.SECONDS,
      STATE_NAME: newCreationFlowEvents.changeTimesliceUnit.state,
    });
  };

  return (
    <tbody>
      <tr>
        <td
          style={{
            padding: '4px 0',
            paddingRight: 16,
            background: '#FFF',
            borderRight: '1px solid #E8E8E8',
          }}
        >
          <div style={{ width: 320, textAlign: 'left' }} />
        </td>
        <Form.List name="configurations">
          {(configurations) =>
            configurations.map((configuration, idx: number) => {
              if (idx > 0) return null;

              return (
                <Form.List
                  name={[configuration.name, 'variables']}
                  key={configuration.key}
                >
                  {(variables) =>
                    variables.map((variable, index: number) => {
                      if (index > 0) return null;

                      return (
                        <Form.List
                          key={variable.key}
                          name={[variable.name, 'choices']}
                        >
                          {(choices) =>
                            choices.map((choice) => {
                              return (
                                <td
                                  key={choice.key}
                                  style={{
                                    padding: '4px 16px',
                                    borderRight: '1px solid #e8e8e8',
                                    position: 'relative',
                                    zIndex: 0,
                                    minWidth: 400,
                                    maxWidth: 400,
                                  }}
                                >
                                  <TimeSliceInputContainer
                                    color={TreatmentDefaultColors[choice.key]}
                                  >
                                    <Typography.Metadata>
                                      Time
                                    </Typography.Metadata>

                                    <TimeConfigGroup>
                                      <Form.Item
                                        name={[choice.name, 'span']}
                                        style={{
                                          margin: 0,
                                          flex: 1,
                                          display: 'block',
                                        }}
                                        rules={[
                                          {
                                            required: true,
                                            message: '',
                                          },
                                        ]}
                                      >
                                        <InputNumber
                                          disabled={readOnly}
                                          type="number"
                                          style={{ width: '100%' }}
                                          min={0}
                                          onChange={() => {
                                            const choiceData =
                                              form.getFieldValue([
                                                'configurations',
                                                configuration.name,
                                                'variables',
                                                variable.name,
                                                'choices',
                                                choice.name,
                                              ]);

                                            trackTimeSliceValueChange(
                                              choiceData,
                                            );
                                          }}
                                        />
                                      </Form.Item>
                                      <Form.Item
                                        name={[choice.name, 'spanUnit']}
                                      >
                                        <Select
                                          disabled={readOnly}
                                          className="timeSliceInput"
                                          defaultValue={
                                            TimeSliceTimeUnit.SECONDS
                                          }
                                          onChange={() => {
                                            const choiceData =
                                              form.getFieldValue([
                                                'configurations',
                                                configuration.name,
                                                'variables',
                                                variable.name,
                                                'choices',
                                                choice.name,
                                              ]);

                                            trackTimeSliceUnitChange(
                                              choiceData,
                                            );
                                          }}
                                          options={[
                                            {
                                              label: 'seconds',
                                              value: TimeSliceTimeUnit.SECONDS,
                                            },
                                            {
                                              label: 'minutes',
                                              value: TimeSliceTimeUnit.MINUTES,
                                            },
                                            {
                                              label: 'hours',
                                              value: TimeSliceTimeUnit.HOURS,
                                            },
                                          ]}
                                        />
                                      </Form.Item>
                                    </TimeConfigGroup>

                                    <Typography.Metadata>
                                      Buffer Time
                                    </Typography.Metadata>

                                    <TimeConfigGroup>
                                      <Form.Item
                                        name={[choice.name, 'spanBuffer']}
                                        style={{
                                          margin: 0,
                                          flex: 1,
                                          display: 'block',
                                        }}
                                        rules={[
                                          {
                                            required: true,
                                            message: '',
                                          },
                                        ]}
                                      >
                                        <InputNumber
                                          disabled={readOnly}
                                          type="number"
                                          min={0}
                                          style={{ width: '100%' }}
                                          onChange={() => {
                                            const choiceData =
                                              form.getFieldValue([
                                                'configurations',
                                                configuration.name,
                                                'variables',
                                                variable.name,
                                                'choices',
                                                choice.name,
                                              ]);

                                            trackTimeSliceValueChange(
                                              choiceData,
                                            );
                                          }}
                                        />
                                      </Form.Item>
                                      <Form.Item
                                        name={[choice.name, 'spanBufferUnit']}
                                      >
                                        <Select
                                          disabled={readOnly}
                                          className="timeSliceInput"
                                          defaultValue={
                                            TimeSliceTimeUnit.SECONDS
                                          }
                                          onChange={() => {
                                            const choiceData =
                                              form.getFieldValue([
                                                'configurations',
                                                configuration.name,
                                                'variables',
                                                variable.name,
                                                'choices',
                                                choice.name,
                                              ]);

                                            trackTimeSliceUnitChange(
                                              choiceData,
                                            );
                                          }}
                                          options={[
                                            {
                                              label: 'seconds',
                                              value: TimeSliceTimeUnit.SECONDS,
                                            },
                                            {
                                              label: 'minutes',
                                              value: TimeSliceTimeUnit.MINUTES,
                                            },
                                            {
                                              label: 'hours',
                                              value: TimeSliceTimeUnit.HOURS,
                                            },
                                          ]}
                                        />
                                      </Form.Item>
                                    </TimeConfigGroup>
                                  </TimeSliceInputContainer>
                                </td>
                              );
                            })
                          }
                        </Form.List>
                      );
                    })
                  }
                </Form.List>
              );
            })
          }
        </Form.List>
      </tr>
    </tbody>
  );
};

const TimesliceValues = () => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const { configurations } = useExperimentContext();

  // Safely calculate AXA compatibility - default to not showing warning on error
  let showAxaSliceWarning = false;
  try {
    const sliceWindowDuration =
      getSequentialTimesliceSliceWindow(configurations);
    showAxaSliceWarning =
      !readOnly && isAxaIncompatibleSliceWindow(sliceWindowDuration);
  } catch (error) {
    console.warn('Error calculating AXA slice window compatibility:', error);
  }

  return (
    <Card>
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <table className="experiment-treatment-table">
          <TimeSliceValuesHeader />
          <TimeSliceValuesBody />
        </table>
        <AxaWarningAlert
          show={showAxaSliceWarning}
          type="sliceWindow"
          style={{ margin: 'auto', marginTop: 10 }}
        />
      </div>
    </Card>
  );
};

export const TreatmentDot = styled.div<{ color: string }>`
  border-radius: 16px;
  height: 16px;
  width: 16px;
  line-height: 0px;
  background-color: ${({ color }: { color: string }) => color};
`;

const Tag = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  height: 40px;
  border-radius: 8px;
  padding: 12px 16px;
  background-color: ${({ color }: { color: string }) => color};
`;

const BaseInputContainer = styled.div`
  border-radius: 14px;
  font-size: 14px;
  border: ${(props) => `1px solid ${props.color}`};
`;

const TimeSliceInputContainer = styled(BaseInputContainer)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border: ${(props) => `1px solid ${props.color}`};
  & .configTitle {
    font-size: 12px;
  }
`;

const TimeConfigGroup = styled.div`
  align-items: start;
  display: flex;
  gap: 8px;
  & .timeSliceInput {
    flex: 1;
  }
`;

export default TimesliceValues;
